import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { resources, SUPPORTED_LANGUAGES, SupportedLanguage } from 'src/i18n/resources'

const LANGUAGE_STORAGE_KEY = 'app_language'

type TranslateParams = Record<string, string | number>

interface II18nContext {
  language: SupportedLanguage
  setLanguage: (language: SupportedLanguage) => void
  t: (key: string, fallback?: string, params?: TranslateParams) => string
}

const I18nContext = createContext<II18nContext | undefined>(undefined)

const getInitialLanguage = (): SupportedLanguage => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as SupportedLanguage | null
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
    return stored
  }

  return 'id'
}

const resolveMessage = (obj: Record<string, any>, key: string): string | undefined => {
  return key.split('.').reduce((acc: any, part: string) => acc?.[part], obj)
}

const interpolate = (message: string, params?: TranslateParams) => {
  if (!params) return message

  return Object.entries(params).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value)),
    message,
  )
}

export const I18nProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage)

  const setLanguage = useCallback((nextLanguage: SupportedLanguage) => {
    setLanguageState(nextLanguage)
    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage)
  }, [])

  const t = useCallback(
    (key: string, fallback?: string, params?: TranslateParams) => {
      const activeDictionary = resources[language] || resources.id
      const rawMessage =
        resolveMessage(activeDictionary, key) ??
        resolveMessage(resources.en, key) ??
        fallback ??
        key

      return interpolate(String(rawMessage), params)
    },
    [language],
  )

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }

  return context
}
